import { useEffect, useState } from 'react';

import { useInteractionContext } from '@atlaskit/react-ufo/interaction-context';

/* This hook takes a hold identifier and returns a wrapper around `fetch` that
 * will track the loading time via UFO.
 *
 * In cases where we don't have a simple `fetch` call, this same pattern may be
 * applied directly.
 *
 * Usage example:
 *
 * import { useTimedFetch } from 'src/hooks/ufo';
 *
 * const sourceFetch = useTimedFetch('source-branches');
 * const response = await sourceFetch(authRequest(url));
 */
export const useTimedFetch = (holdName: string): typeof fetch => {
  const ufoContext = useInteractionContext();
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (isLoading) {
      // Important to note that we must return the call to `hold` or the
      // interaction will never complete.
      return ufoContext?.hold(holdName);
    }
    return () => {
      /* ESLint expects a consistent return value */
    };
  }, [ufoContext, isLoading, holdName]);

  return async (request: Request): Promise<Response> => {
    setLoading(true);
    const response = await fetch(request);
    setLoading(false);
    return response;
  };
};

/* This hook tracks the given hold name with UFO for as long as `isLoading` is
 * true.
 *
 * Usage example:
 *
 * import { useTrackLoadingState } from 'src/hooks/ufo';
 *
 * const [isLoading, setIsLoading] = useState(false);
 * useTrackLoadingState('loading-diff', isLoading);
 */
export const useTrackLoadingState = (
  holdName: string,
  isLoading: boolean
): void => {
  const ufoContext = useInteractionContext();

  useEffect(() => {
    if (isLoading) {
      return ufoContext?.hold(holdName);
    }
    return () => {
      /* ESLint expects a consistent return value */
    };
  }, [ufoContext, holdName, isLoading]);
};
